import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Building2, ChevronDown, Loader2 } from 'lucide-react';
import { fetchBrands } from '../../store/slices/brandsSlice';
import { setShowAddProjectModal } from '../../store/slices/modalsSlice';
import LoadingBrands from './LoadingBrands';
import BrandCard from './BrandCard';

const PAGE_SIZE = 5;

const BrandsSection = () => {
  const dispatch = useDispatch();
  const { brands, loading, loadingMore, total, error } = useSelector(state => state.brands);

  // Initial load: only the first page. The dashboard fan-out (analytics +
  // lost-affiliate calls) only fires for cards that mount, so this also caps
  // the per-card request storm on initial paint.
  useEffect(() => {
    dispatch(fetchBrands({ limit: PAGE_SIZE, offset: 0 }));
  }, [dispatch]);

  const handleAddProject = () => {
    dispatch(setShowAddProjectModal(true));
  };

  const handleRetry = () => {
    dispatch(fetchBrands({ limit: PAGE_SIZE, offset: 0 }));
  };

  const handleLoadMore = () => {
    dispatch(fetchBrands({ limit: PAGE_SIZE, offset: brands.length, append: true }));
  };

  // total === null means we don't know the count yet (legacy non-paginated
  // response). In that case never show the load-more button.
  const hasMore = typeof total === 'number' && brands.length < total;

  return (
    <div className="mb-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-0.5">Your Projects</h2>
          <p className="text-gray-500 dark:text-slate-400 text-xs">Monitor and manage your affiliate brands</p>
        </div>
      </div>

      {loading && <LoadingBrands />}

      {!loading && error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <div className="text-red-800 dark:text-red-400">
            <strong>Error:</strong> {error}
          </div>
          <button
            onClick={handleRetry}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      )}

      {!loading && !error && brands.length === 0 && (
        <div className="text-center py-12 bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700">
          <Building2 className="w-16 h-16 text-gray-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100 mb-2">No brands found</h3>
          <p className="text-gray-600 dark:text-slate-400 mb-4">Get started by adding your first brand</p>
          <button
            onClick={handleAddProject}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Add Your First Brand
          </button>
        </div>
      )}

      {!loading && !error && brands.length > 0 && (
        <>
          <div className="space-y-3">
            {brands.map((brand) => (
              <BrandCard key={brand.id} brand={brand} />
            ))}
          </div>
          {hasMore && (
            <div className="mt-4 flex flex-col items-center gap-2">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm"
              >
                {loadingMore ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading…
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    Load {Math.min(PAGE_SIZE, total - brands.length)} more
                  </>
                )}
              </button>
              <p className="text-xs text-gray-500 dark:text-slate-400">
                Showing {brands.length} of {total} brands
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BrandsSection;
